import {
  type Bounds,
  EDGE_LABEL_FONT,
  EDGE_LABEL_FONT_SIZE,
  type EdgeCenterLabelValue,
  type EdgeData,
  type EdgeId,
  type EdgeLabels,
  type EdgeLinecapLabelValue,
  type EdgePositionsArrowHead,
  type EdgePositionsCircle,
  type EdgePositionsLabel,
  type EdgePositionsLabels,
  type EdgePositionsLine,
  type EdgeRecord,
  type LinePattern,
  type Point,
  type RGBColorNumber,
  type RenderableEdgeData,
  brand,
  createEdgeHitArea,
  getEdgeCenterLabelText,
  getEdgeLinecapLabelText,
  getEdgePositionsBounds,
  getEdgeStyleArrowHeadColor,
  getEdgeStyleCircleColor,
  getEdgeStyleLineColor,
  getEdgeStyleLinePattern,
  getEdgeStyleThickness,
  isEdgeLinePoint,
  isRenderableEdgeData,
} from '@ipfabric/utils';
import { plainMap } from '@ipfabric/utils/fp/plainMap';
import { maybePropOf } from '@ipfabric/utils/fp/prop';
import {
  type GraphDisplayObject,
  createGraphDisplayObject,
} from 'components/Graph/GraphApp/helpers/createGraphDisplayObject';
import type {
  EdgeLabelReasonToHide,
  EdgeReasonToHide,
} from 'components/Graph/GraphApp/hideReasons';
import type { GraphRendererPluginInterface } from 'components/Graph/GraphApp/plugins/GraphRendererPlugin';
import {
  ZINDEX_EDGE,
  ZINDEX_EDGE_LABELS,
  ZINDEX_EDGE_LINE,
} from 'components/Graph/GraphApp/zIndexes';
import { array, option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { flow, pipe } from 'fp-ts/lib/function';
import { BitmapText, Container, Graphics, Sprite, Texture } from 'pixi.js';
import {
  type CompositeHiding,
  installCompositeHiding,
} from 'utils/pixi/compositeHiding';
import { getBitmapTextAnchor } from 'utils/pixi/getBitmapTextAnchor';
import { isFontAvailable } from 'utils/pixi/isFontAvailable';
import { prefixer } from 'utils/prefixer';
import { drawDashedLine } from './helpers/drawDashedLine';
import { drawDottedLine } from './helpers/drawDottedLine';
import { drawQuadraticCurve } from './helpers/drawQuadraticCurve';

export type GraphEdgeDisplayObject = GraphDisplayObject<
  'edge',
  EdgeReasonToHide
>;
export type GraphEdgeLabelsDisplayObject = Container &
  CompositeHiding<EdgeLabelReasonToHide>;

const EDGE_LINE_NAME = 'edgeLine';
const EDGE_LABELS_NAME = 'edgeLabels';

const getPreviousPoint = (line: EdgePositionsLine, index: number): Point => {
  // Fixed noUncheckedIndexedAccess NIM-19408
  const node = line[index]!;
  return isEdgeLinePoint(node) ? node : node.e;
};

const drawSolidLine = (edgeLine: Graphics, node: Point) => {
  edgeLine.lineTo(node.x, node.y);
};

const drawStraightSegmentLine = (
  edgeLine: Graphics,
  line: EdgePositionsLine,
  node: Point,
  thickness: number,
  pattern: LinePattern,
  color: number,
  index: number,
): void => {
  switch (pattern) {
    case 'solid':
      drawSolidLine(edgeLine, node);
      break;
    case 'dashed': {
      const previousPoint = getPreviousPoint(line, index);
      if (previousPoint) {
        drawDashedLine(edgeLine, previousPoint, node, thickness);
      }
      break;
    }
    case 'dotted': {
      const previousPoint = getPreviousPoint(line, index);
      if (previousPoint) {
        drawDottedLine(edgeLine, previousPoint, node, thickness, color);
      }
      break;
    }
    default:
      break;
  }
};

export const drawEdgeLine = (
  edgeLine: Graphics,
  line: EdgePositionsLine,
  thickness: number,
  pattern: LinePattern,
  color: number,
): void => {
  const startPoint = line[0];
  edgeLine.moveTo(startPoint.x, startPoint.y);

  line.slice(1).forEach((node, index) => {
    if (isEdgeLinePoint(node)) {
      drawStraightSegmentLine(
        edgeLine,
        line,
        node,
        thickness,
        pattern,
        color,
        index,
      );
    } else {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const previousPoint = line[index]!;
      const previousStartPoint = getPreviousPoint(line, index);
      if (previousStartPoint) {
        drawQuadraticCurve(
          edgeLine,
          previousStartPoint,
          node.c,
          node.e,
          pattern,
          thickness,
          color,
          isEdgeLinePoint(previousPoint) ? 1 : 0,
        );
      }
    }
  });
};

export const drawEdgeCircle = (
  edgeLine: Graphics,
  circle: EdgePositionsCircle,
  circleColor: RGBColorNumber,
): void => {
  edgeLine.lineStyle(0);
  edgeLine.beginFill(circleColor, 1);
  edgeLine.drawCircle(circle.position.x, circle.position.y, circle.radius);
  edgeLine.endFill();
};

export const drawEdgeArrow = (
  edgeLine: Graphics,
  arrowHead: EdgePositionsArrowHead | undefined,
  arrowHeadColor: number,
  thickness?: number,
): void => {
  if (arrowHead === undefined) {
    return;
  }
  edgeLine.lineStyle(thickness ?? 0, arrowHeadColor, 1);
  edgeLine.beginFill(arrowHeadColor, 1);
  edgeLine.moveTo(arrowHead[0].x, arrowHead[0].y);
  // Fixed noUncheckedIndexedAccess NIM-19408
  edgeLine.lineTo(arrowHead[1]!.x, arrowHead[1]!.y);
  // Fixed noUncheckedIndexedAccess NIM-19408
  edgeLine.lineTo(arrowHead[2]!.x, arrowHead[2]!.y);
  edgeLine.closePath();
  edgeLine.endFill();
};

const drawLabel =
  (edgeLabels: Container, positions: EdgePositionsLabel) =>
  (text: string): Container => {
    const container = new Container();
    const background = new Sprite(Texture.WHITE);
    const labelText = new BitmapText(text, {
      font: { name: EDGE_LABEL_FONT, size: EDGE_LABEL_FONT_SIZE },
    });

    background.width = labelText.textWidth;
    background.height = labelText.textHeight;
    background.anchor.set(positions.anchor.x, positions.anchor.y);
    getBitmapTextAnchor(labelText).set(positions.anchor.x, positions.anchor.y);

    container.addChild(background, labelText);
    container.x = positions.position.x;
    container.y = positions.position.y;
    container.angle = positions.angle;

    edgeLabels.addChild(container);

    return container;
  };

const drawLabelIfAvailable = <
  T extends EdgeCenterLabelValue | EdgeLinecapLabelValue,
>(
  edgeLabels: Container,
  texts: T[] | undefined,
  getText: (labels: T[]) => string | null,
  positions: EdgePositionsLabel | undefined,
): Option<Container> =>
  positions
    ? pipe(
        texts,
        option.fromNullable,
        option.chain(flow(getText, option.fromNullable)),
        option.map(drawLabel(edgeLabels, positions)),
      )
    : option.none;

const drawEdgeLabels = (
  edgeLabels: Container,
  labelPositions: EdgePositionsLabels | undefined,
  labelTexts: EdgeLabels,
): void => {
  if (!isFontAvailable(EDGE_LABEL_FONT)) {
    return;
  }
  if (labelPositions === undefined) {
    return;
  }
  drawLabelIfAvailable(
    edgeLabels,
    labelTexts.source,
    getEdgeLinecapLabelText,
    labelPositions.source,
  );
  drawLabelIfAvailable(
    edgeLabels,
    labelTexts.center,
    getEdgeCenterLabelText,
    labelPositions.center,
  );
  drawLabelIfAvailable(
    edgeLabels,
    labelTexts.target,
    getEdgeLinecapLabelText,
    labelPositions.target,
  );
};

const [idToName, nameToId] = prefixer('e/', (str) => {
  throw new Error(`Invalid edge name ${str}`);
});

export const getEdgeId = (edgeDisplayObject: GraphEdgeDisplayObject): EdgeId =>
  brand<EdgeId>(nameToId(edgeDisplayObject.name));

export function createGraphEdgeDisplayObject(
  id: EdgeId,
  data: EdgeData,
  pluginInterface: GraphRendererPluginInterface,
): GraphEdgeDisplayObject {
  const edge: GraphEdgeDisplayObject = createGraphDisplayObject(
    pluginInterface,
    'edge',
    () =>
      pluginInterface.isPeekingAtHiddenNodes()
        ? emitEdgeUnhiddenBoundsChange(pluginInterface, id, edge)
        : emitEdgeHiddenBoundsChange(pluginInterface, id, edge),
    ['SOURCE_NODE_HIDDEN', 'TARGET_NODE_HIDDEN'],
  );
  edge.name = idToName(id);
  edge.zIndex = ZINDEX_EDGE;
  edge.interactive = true;
  // Sort items by indexes to allow adding highlighted edge line with animation
  edge.sortableChildren = true;

  const edgeLine = new Graphics();
  edgeLine.name = EDGE_LINE_NAME;
  // Default line will be always at the bottom
  edgeLine.zIndex = ZINDEX_EDGE_LINE;
  edge.addChild(edgeLine);

  const edgeLabels = installCompositeHiding(new Container());
  edgeLabels.name = EDGE_LABELS_NAME;
  // Labels must be above highlighted edge line
  edgeLabels.zIndex = ZINDEX_EDGE_LABELS;
  edge.addChild(edgeLabels);

  if (isRenderableEdgeData(data)) {
    updateGraphEdgeDisplayObject(edge, data);
  }

  return edge;
}

export function updateGraphEdgeDisplayObject(
  edge: GraphEdgeDisplayObject,
  data: RenderableEdgeData,
): void {
  const thickness = getEdgeStyleThickness(data.style);
  edge.hitArea = createEdgeHitArea(data.positions, thickness);

  const edgeLine = edge.getChildByName(EDGE_LINE_NAME) as Graphics;
  const edgeLabels = getEdgeLabelsDisplayObject(edge);
  edgeLine.clear();
  edgeLabels.removeChildren();

  const color = getEdgeStyleLineColor(data.style);
  edgeLine.lineStyle(thickness, color);
  const linePattern = getEdgeStyleLinePattern(data.style);
  drawEdgeLine(edgeLine, data.positions.line, thickness, linePattern, color);

  drawEdgeArrow(
    edgeLine,
    data.positions.arrowHeads?.source,
    getEdgeStyleArrowHeadColor('source', data.style),
  );
  drawEdgeArrow(
    edgeLine,
    data.positions.arrowHeads?.target,
    getEdgeStyleArrowHeadColor('target', data.style),
  );
  if (data.positions.circle) {
    drawEdgeCircle(
      edgeLine,
      data.positions.circle,
      getEdgeStyleCircleColor(data.style),
    );
  }

  if (data.labels) {
    drawEdgeLabels(edgeLabels, data.positions.labels, data.labels);
  }
}

export function getEdgeLabelsDisplayObject(
  edge: GraphEdgeDisplayObject,
): GraphEdgeLabelsDisplayObject {
  return edge.getChildByName(EDGE_LABELS_NAME) as GraphEdgeLabelsDisplayObject;
}

export function setLabelsVisible(
  edge: GraphEdgeDisplayObject,
  visible: boolean,
  reason: EdgeLabelReasonToHide,
): void {
  getEdgeLabelsDisplayObject(edge).reasonsToHide[visible ? 'delete' : 'add'](
    reason,
  );
}

export function getEdgeBounds(
  edgeData: EdgeData,
  edge: GraphEdgeDisplayObject,
  evenIfHidden?: true,
): Option<Bounds> {
  if (!evenIfHidden && edge.isHiddenFromBounds()) {
    return option.none;
  }
  return pipe(
    edgeData.positions,
    option.fromNullable,
    option.map(getEdgePositionsBounds),
  );
}

const emitEdgeHiddenBoundsChange = (
  pluginInterface: GraphRendererPluginInterface,
  edgeId: EdgeId,
  edge: GraphEdgeDisplayObject,
) => {
  const edgeData = pluginInterface.getGraphData().edges[edgeId];

  if (edgeData) {
    pluginInterface.events.emit('edgeBoundsChange', {
      edge,
      edgeData,
      bounds: option.none,
    });
  }
};

export function hideEdge(
  pluginInterface: GraphRendererPluginInterface,
  edgeId: EdgeId,
  reason: EdgeReasonToHide,
): void {
  const edge = pluginInterface.edgeDisplayObjects.get(edgeId);
  if (edge !== undefined) {
    const wasHiddenFromBounds = edge.isHiddenFromBounds();
    edge.reasonsToHide.add(reason);
    if (!wasHiddenFromBounds && edge.isHiddenFromBounds()) {
      emitEdgeHiddenBoundsChange(pluginInterface, edgeId, edge);
    }
  }
}

const emitEdgeUnhiddenBoundsChange = (
  pluginInterface: GraphRendererPluginInterface,
  edgeId: EdgeId,
  edge: GraphEdgeDisplayObject,
) => {
  // Fixed noUncheckedIndexedAccess NIM-19408
  const edgeData = pluginInterface.getGraphData().edges[edgeId]!;
  const bounds = getEdgeBounds(edgeData, edge, true);

  pluginInterface.events.emit('edgeBoundsChange', { edge, edgeData, bounds });
};

function unhideEdge(
  pluginInterface: GraphRendererPluginInterface,
  edgeId: EdgeId,
  reason: EdgeReasonToHide,
): void {
  const edge = pluginInterface.edgeDisplayObjects.get(edgeId);
  if (edge !== undefined) {
    const wasHiddenFromBounds = edge.isHiddenFromBounds();
    edge.reasonsToHide.delete(reason);
    if (wasHiddenFromBounds && !edge.isHiddenFromBounds()) {
      emitEdgeUnhiddenBoundsChange(pluginInterface, edgeId, edge);
    }
  }
}

export function toggleHideEdge(
  pluginInterface: GraphRendererPluginInterface,
  edgeId: EdgeId,
  reason: EdgeReasonToHide,
  hide: boolean,
): void {
  (hide ? hideEdge : unhideEdge)(pluginInterface, edgeId, reason);
}

/**
 * Used only for ShowEdgeDetailCommand input, which should adopt new labels format
 * @deprecated
 */
export const labelsToStringArray = (
  labels?: EdgeLabels,
): [string, string, string] => {
  const labelToText = <T extends EdgeLinecapLabelValue | EdgeCenterLabelValue>(
    label: T[] | undefined,
    getText: (l: T[]) => string | null,
  ): string =>
    pipe(
      label,
      option.fromNullable,
      option.map((labelItems) =>
        labelItems.map((item) => ({ ...item, visible: true })),
      ),
      option.chain(flow(getText, option.fromNullable)),
      option.getOrElse(() => ''),
    );
  return [
    labelToText(labels?.source, getEdgeLinecapLabelText),
    labelToText(labels?.center, getEdgeCenterLabelText),
    labelToText(labels?.target, getEdgeLinecapLabelText),
  ];
};

export const edgeObjectsFromIds = (
  edgeDisplayObjects: Map<EdgeId, GraphEdgeDisplayObject>,
): ((edgeIds: EdgeId[]) => GraphEdgeDisplayObject[]) =>
  array.filterMap(plainMap.getFrom(edgeDisplayObjects));

export const edgesDataFromIds = (
  edgeRecord: EdgeRecord,
): ((edgeIds: EdgeId[]) => EdgeData[]) =>
  array.filterMap(maybePropOf(edgeRecord));

export const isLoopbackEdge = ({ source, target }: EdgeData): boolean =>
  source === target;
